import React, { useRef, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';

import { REACT_APP_LOGIN_AUTH } from '../../../../../constants/envConstants';
import { encrypt } from '../../../../../utils/security.helper';

import { extractPinVerificationDigit } from './helper/helper';
import { FormValues, MpinProps } from './Interface/setup-mpin.interface';

const MPIN_LENGTH = 4;

const Mpin: React.FC<MpinProps> = ({ handleCloseDialogue }) => {
  const toast = useRef<Toast | null>(null);

  const [showMpin, setShowMpin] = useState<boolean>(true);
  const [showConfirmMpin, setShowConfirmMpin] = useState<boolean>(true);

  // Refs to hold digits (same as MpinLogin)
  const pinRef = useRef<string[]>(Array(MPIN_LENGTH).fill(''));
  const confirmPinRef = useRef<string[]>(Array(MPIN_LENGTH).fill(''));

  /* -------------------- Formik -------------------- */
  const formik = useFormik<FormValues>({
    initialValues: {
      pin: '',
      confirm_pin: '',
    },
    validationSchema: Yup.object({
      pin: Yup.string()
        .matches(/^\d+$/, 'PIN must contain digits only')
        .length(MPIN_LENGTH, 'Pin must be 4 digits')
        .required('Pin is required'),

      confirm_pin: Yup.string()
        .oneOf([Yup.ref('pin')], 'PIN and Confirm PIN does not match')
        .required('Please confirm your Pin'),
    }),
    onSubmit: async values => {
      try {
        const response = await axios.post(
          `${REACT_APP_LOGIN_AUTH}/auth/add-mpin`,
          {
            mpin: encrypt(values.pin),
            confirmMpin: encrypt(values.confirm_pin),
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('access_token')}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (response?.data?.status === true) {
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: response?.data?.message ?? 'Pin set successfully',
          });

          localStorage.removeItem('wantSetupMpin');
          localStorage.setItem('is_mpin_enabled', 'true');

          setTimeout(() => {
            handleCloseDialogue(false);
          }, 1500);
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: response?.data?.message ?? 'Something went wrong',
          });
        }
      } catch (err) {
        const error = err as { response?: { data?: { message?: string } } };
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: error?.response?.data?.message
            ? error?.response?.data?.message
            : 'SomeThing Went wrong',
        });

        setTimeout(() => {
          handleCloseDialogue(false);
        }, 1500);
      }
    },
  });

  /* -------------------- Handlers -------------------- */

  const handleOtpChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    idx: number,
    ref: React.MutableRefObject<string[]>,
    field: 'pin' | 'confirm_pin'
  ) => {
    const setMpinDigits = extractPinVerificationDigit(e?.target?.value);
    ref.current[idx] = setMpinDigits;
    e.target.value = setMpinDigits;

    formik.setFieldTouched(field, true, true);
    formik.setFieldValue(field, ref.current.join(''));

    if (setMpinDigits && idx < MPIN_LENGTH - 1) {
      document.getElementById(`${field}-otp-${idx + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    idx: number,
    ref: React.MutableRefObject<string[]>,
    field: 'pin' | 'confirm_pin'
  ) => {
    if (e.key === 'Backspace') {
      ref.current[idx] = '';

      if (idx > 0 && !e.currentTarget.value) {
        document.getElementById(`${field}-otp-${idx - 1}`)?.focus();
      }
    }

    formik.setFieldValue(field, ref.current.join(''));
  };

  /* -------------------- UI -------------------- */

  return (
    <>
      <Toast ref={toast} />

      <Card className="w-full px-4 py-1 shadow-3 border-round-2xl">
        <h3 className="text-center mb-4">Set-up your Pin</h3>

        <form onSubmit={formik.handleSubmit} autoComplete="off">
          <div className="grid justify-content-center">
            {/* ---------------- PIN ---------------- */}
            <div className="col-12">
              <label htmlFor="m_pin" className="text-base">
                Pin<span style={{ color: 'red' }}>*</span>
              </label>

              <div className="flex align-items-center gap-2 mt-2 w-full">
                {Array.from({ length: MPIN_LENGTH }).map((_, idx) => (
                  <input
                    key={idx}
                    id={`pin-otp-${idx}`}
                    type={showMpin ? 'text' : 'password'}
                    value={pinRef.current[idx] || ''}
                    maxLength={1}
                    className="border-1 border-gray-400 border-round-sm
                                outline-none shadow-none
                                focus:outline-none focus:shadow-none focus:border-gray-400
                                text-center h-3rem flex-1 min-w-0"
                    onChange={e => handleOtpChange(e, idx, pinRef, 'pin')}
                    onKeyDown={e => handleOtpKeyDown(e, idx, pinRef, 'pin')}
                  />
                ))}

                <i
                  className={`pi ${showMpin ? 'pi-eye-slash' : 'pi-eye'} ml-3`}
                  role="button"
                  tabIndex={0}
                  onClick={() => setShowMpin(prev => !prev)}
                  style={{ cursor: 'pointer' }}
                />
              </div>

              {formik.touched.pin && formik.errors.pin && (
                <div className="text-red-500 text-xs mt-1">
                  {formik.errors.pin}
                </div>
              )}
            </div>

            {/* ---------------- CONFIRM PIN ---------------- */}
            <div className="col-12 mt-3">
              <label htmlFor="confirm_pin" className="text-base">
                Confirm Pin<span style={{ color: 'red' }}>*</span>
              </label>

              <div className="flex align-items-center gap-2 mt-2 w-full">
                {Array.from({ length: MPIN_LENGTH }).map((_, idx) => (
                  <input
                    key={idx}
                    id={`confirm_pin-otp-${idx}`}
                    type={showConfirmMpin ? 'text' : 'password'}
                    value={confirmPinRef.current[idx] || ''}
                    maxLength={1}
                    className="border-1 border-gray-400 border-round-sm
                                outline-none shadow-none
                                focus:outline-none focus:shadow-none focus:border-gray-400
                                text-center h-3rem flex-1 min-w-0"
                    onChange={e =>
                      handleOtpChange(e, idx, confirmPinRef, 'confirm_pin')
                    }
                    onKeyDown={e =>
                      handleOtpKeyDown(e, idx, confirmPinRef, 'confirm_pin')
                    }
                  />
                ))}

                <i
                  className={`pi ${
                    showConfirmMpin ? 'pi-eye-slash' : 'pi-eye'
                  } ml-3`}
                  role="button"
                  tabIndex={0}
                  onClick={() => setShowConfirmMpin(prev => !prev)}
                  style={{ cursor: 'pointer' }}
                />
              </div>

              {formik.touched.confirm_pin && formik.errors.confirm_pin && (
                <div className="text-red-500 text-xs mt-1">
                  {formik.errors.confirm_pin}
                </div>
              )}
            </div>

            {/* ---------------- SUBMIT ---------------- */}
            <div className="col-12 mt-4">
              <Button
                label="Submit"
                type="submit"
                className="w-full"
                disabled={
                  formik.values.pin.length !== MPIN_LENGTH ||
                  formik.values.confirm_pin.length !== MPIN_LENGTH ||
                  formik.values.pin !== formik.values.confirm_pin
                }
              />
            </div>
          </div>
        </form>
      </Card>
    </>
  );
};

export default Mpin;
